import * as moment from 'moment';

import 'moment-timezone';

export const getTimeZoneOffsetAsString = () => {
  let timeZoneInMinutes = new Date().getTimezoneOffset();
  let hours;
  let minutes;
  let offSetSign;
  if (timeZoneInMinutes < 0) {
    hours = Math.floor(-timeZoneInMinutes / 60);
    minutes = -timeZoneInMinutes % 60;
    offSetSign = '+';
  } else {
    hours = Math.floor(timeZoneInMinutes / 60);
    minutes = timeZoneInMinutes % 60;
    offSetSign = '-';
  }
  let formattedHours = hours < 10 ? '0' + hours : hours;
  let formattedMinutes = minutes < 10 ? '0' + minutes : minutes;
  let timeZoneOffSet = offSetSign + formattedHours + ':' + formattedMinutes;
  return timeZoneOffSet;
};

export const currentBrowserTimeZone = moment.tz.guess();

const dateTimeOffset = (timeZone, date, format) => {
  let getDate = moment(date)
    .tz(timeZone || currentBrowserTimeZone)
    .format(format);
  return `${getDate}`;
};

export const handleDateTimeOffset = (timeZone, lastUpdateTime = new Date(), format = 'MM/DD/YYYY[,] hh:mm a') => {
  return dateTimeOffset(timeZone ? timeZone : currentBrowserTimeZone, lastUpdateTime ? lastUpdateTime : new Date(), format);
};

export const convertToTimezoneFormat = (date, timezone) => {
  return moment(date)
    ?.tz(timezone || currentBrowserTimeZone, false)
    ?.format();
};

export const convertToTimezoneWithStartOfDayFormat = (date, timezone) => {
  return moment(date)
    ?.startOf('day')
    ?.tz(timezone || currentBrowserTimeZone, true)
    ?.format();
};

export const convertToTimezoneWithStartOfDayFormatWithCurrentTime = (date, timezone) => {
  const now = moment();
  // Take the selected date, set it to current browser time, then convert to target timezone
  // Do NOT use keepTime=true parameter - we want actual timezone conversion
  return moment(date)
    ?.hour(now.hour())
    ?.minute(now.minute())
    ?.second(now.second())
    ?.tz(timezone || currentBrowserTimeZone)
    ?.format();
};

export const convertToTimezoneWithEndOfDayFormat = (date, timezone) => {
  return moment(date)
    ?.endOf('day')
    ?.tz(timezone || currentBrowserTimeZone, true)
    ?.format();
};

export const getTimezoneOffset = (timezone) => {
  // Get the current date-time in the specified timezone
  var currentTime = moment?.tz(timezone);

  // Format the offset in hours and minutes (e.g., +05:30)
  var offset = currentTime?.format('Z');

  return offset;
};

// Function to calculate the number of whole days difference, including time
export const calculateDaysPastDue = (dueDate, timeZone, includeZeroDays = false) => {
  // Convert dueDate to the given time zone
  const dueDateTime = moment.tz(dueDate, timeZone);
  // Get the current date and time in the given time zone
  const currentDate = moment.tz(timeZone);

  // Calculate the difference in days including fractional days
  const daysDifference = currentDate.diff(dueDateTime, 'days', true);

  // Get the whole number of days difference
  const wholeDaysDifference = Math.floor(daysDifference); // or Math.round(daysDifference)

  if (wholeDaysDifference > 0 || includeZeroDays) {
    return `${wholeDaysDifference} day${wholeDaysDifference > 1 ? 's' : ''}`;
  } else {
    return '-';
  }
};

export const isTaskOverdue = (dueDate, timeZone) => {
  // Convert dueDate to the given time zone
  const dueDateTime = moment.tz(dueDate, timeZone);
  // Get the current date and time in the given time zone
  const currentDate = moment.tz(timeZone);

  return dueDateTime.isBefore(currentDate, 'day');
};

export const convertToUTC = (timezone) => {
  return moment()
    .tz(timezone || currentBrowserTimeZone, true)
    .utc()
    .format('YYYY-MM-DDTHH:mm:ss.SSSSSSZ');
};

export const convertToTimezoneFormatEndOfDay = (date, timezone) => {
  return moment
    .tz(date, timezone || currentBrowserTimeZone)
    .endOf('day')
    .format();
};

export const convertToSpecificTimeZoneFormat = (date, timezone) => {
  return moment(date)
    .tz(timezone || currentBrowserTimeZone, true)
    .format('MM/DD/YYYY, hh:mmA');
};

export const roundToNextHalfHour = (date) => {
  const roundedDate = new Date(date.getTime());
  const minutes = roundedDate.getMinutes();

  if (minutes === 0 || minutes === 30) {
    roundedDate.setSeconds(0, 0);
    return roundedDate;
  }

  roundedDate.setMinutes(minutes + 30 - (minutes % 30));
  roundedDate.setSeconds(0, 0);
  return roundedDate;
};

export const setTimezoneWithoutAlteringTime = (dateInput, targetTimezone = currentBrowserTimeZone) => {
  if (!dateInput || !moment(dateInput).isValid()) return null;
  const local = moment(dateInput);

  // Extract literal date and time values
  const timeParts = {
    year: local.year(),
    month: local.month(),
    day: local.date(),
    hour: local.hour(),
    minute: local.minute(),
    second: local.second(),
  };

  // Now construct the same exact time but attach the target timezone
  const withTargetZone = moment.tz(timeParts, targetTimezone);

  return withTargetZone.format(); // ISO string with correct target timezone offset
};

export const getDateBoundaryInTimezone = (dateInput, timezone = currentBrowserTimeZone, position = 'start', format = undefined) => {
  if (!dateInput || !timezone) return null;

  const date = moment(dateInput).format('YYYY-MM-DD'); // strip time
  const zoned = moment.tz(date, timezone);

  const result = position === 'start' ? zoned.startOf('day') : zoned.endOf('day');
  return format ? result.format(format) : result.format(); // default ISO format
};

export const formatUtcToUserLocalDatetime = (utcDateInput, userTimezone = currentBrowserTimeZone) => {
  if (!utcDateInput || !userTimezone) return '';

  return moment.utc(utcDateInput).tz(userTimezone).format('YYYY-MM-DDTHH:mm');
};

export function convertLocalToZoneStartOfDay(datetimeISO, toZone) {
  // Parse as local time (uses system's local timezone)
  const localTime = moment(datetimeISO);

  // Keep same clock time, reinterpret in target timezone
  const sameClockTimeInTargetZone = moment.tz(
    {
      year: localTime.year(),
      month: localTime.month(),
      date: localTime.date(),
      hour: localTime.hour(),
      minute: localTime.minute(),
      second: localTime.second(),
    },
    toZone
  );

  // Make it start of that day in target zone
  return sameClockTimeInTargetZone.startOf('day').format();
}

export function convertLocalToZoneEndOfDay(datetimeISO, toZone) {
  // Parse as local time (uses system's local timezone)
  const localTime = moment(datetimeISO);

  // Keep same clock time, reinterpret in target timezone
  const sameClockTimeInTargetZone = moment.tz(
    {
      year: localTime.year(),
      month: localTime.month(),
      date: localTime.date(),
      hour: localTime.hour(),
      minute: localTime.minute(),
      second: localTime.second(),
    },
    toZone
  );

  // Make it start of that day in target zone
  return sameClockTimeInTargetZone.endOf('day').format();
}

export const convertToUserTimezoneDateOnly = (dateString, userTimezone) => {
  if (!dateString) return null;

  if (userTimezone) {
    const parsedMoment = moment(dateString);

    const dateInUserTimezone = parsedMoment.tz(userTimezone);

    const year = dateInUserTimezone.year();
    const month = dateInUserTimezone.month();
    const day = dateInUserTimezone.date();

    return new Date(year, month, day);
  } else {
    const datePart = dateString.split('T')[0];
    const [year, month, day] = datePart.split('-');
    return new Date(year, month - 1, day);
  }
};
